/**
 * A-ljus additions to the Opus cart drawer app (renders inside a shadow DOM):
 *  - compact free-shipping bar replacing Opus' own bar
 *  - "Frakt" + "Beräknad leverans" rows above the subtotal ("Summa")
 *  - compact styling for the cart items
 * The markup is rendered by sections/cart-delivery-estimate.liquid so the
 * shipping and dropship logic stays in Liquid.
 */
(() => {
  const SECTION = 'cart-delivery-estimate';
  const STYLE_ID = 'aljus-opus-overrides';
  const CONTINUE_URL = 'collections/all';

  // Each part: where it goes inside the drawer (first matching selector wins)
  const PARTS = [
    {
      mark: 'data-opus-shipping-bar',
      anchors: ['.cd-rewards-container', '.cd-product-list'],
    },
    {
      mark: 'data-opus-delivery-estimate',
      anchors: [
        '.opus-subtotal-section',
        '.op-subtotal',
        '.cd-checkout-section-button',
        '.opus-btn-checkout',
        '.opus-checkout-btn',
      ],
    },
  ];

  const CSS = `
    /* Hide Opus' own free-shipping bar (replaced by .ad-ship) */
    .cd-rewards-container { display: none !important; }

    /* Free-shipping bar */
    .ad-ship {
      margin: 12px 16px 0;
      padding: 12px 0 0;
      font-size: 14px;
      line-height: 1.3;
      color: #1a1a1a;
    }
    .ad-ship__top { display: flex; align-items: center; gap: 8px; }
    .ad-ship__truck { width: 20px; height: 20px; flex-shrink: 0; }
    .ad-ship__check {
      display: flex; align-items: center; justify-content: center;
      width: 22px; height: 22px; flex-shrink: 0;
      border-radius: 50%; background: #5f9c6c; color: #fff;
    }
    .ad-ship__check svg { width: 14px; height: 14px; }
    .ad-ship__text { flex: 1; min-width: 0; }
    .ad-ship__text strong { font-weight: 700; }
    .ad-ship__track {
      height: 8px; margin: 8px 0 0;
      border-radius: 999px; background: #dcdcd8; overflow: hidden;
    }
    .ad-ship__fill {
      display: block; height: 100%;
      border-radius: 999px; background: #6a9f76;
      transition: width 0.4s ease;
    }
    .ad-ship__note { flex-shrink: 0; font-size: 12px; color: rgba(0, 0, 0, 0.6); white-space: nowrap; }

    /* Fresh numbers are loading */
    .ad-ship, .ad-summary { transition: opacity 0.15s ease; }
    .ad-ship.is-updating, .ad-summary.is-updating { opacity: 0.45; }

    /* Order summary above the checkout button (replaces Opus' own subtotal row) */
    .opus-subtotal-section, .op-subtotal { display: none !important; }
    .ad-summary {
      display: flex; flex-direction: column; gap: 6px;
      margin: 0 0 12px;
      font-size: 14px; line-height: 1.4; color: rgba(0, 0, 0, 0.7);
    }
    .ad-summary__row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
    .ad-summary__row span:last-child { text-align: right; color: #1a1a1a; }
    .ad-summary__free { color: #2e7d32 !important; font-size: 13px; }
    .ad-summary__total {
      margin-top: 4px; padding-top: 10px;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      font-size: 17px; font-weight: 700; color: #1a1a1a;
    }
    .ad-summary__eta {
      display: flex; align-items: center; gap: 8px;
      margin: 6px 0 0; font-size: 13px; color: rgba(0, 0, 0, 0.7);
    }
    .ad-summary__eta svg { width: 18px; height: 18px; flex-shrink: 0; color: #1a1a1a; }

    /* Compact cart items: image | title + price / qty stepper + delete */
    .cd-product-item { margin-left: 0 !important; margin-right: 0 !important; }
    .cd-product-list { gap: 12px !important; padding-top: 12px !important; }
    .cd-product-item-content { position: relative !important; align-items: center; }

    .cd-product-item-content .opus-product-item-image-container {
      width: 76px !important; height: 76px !important;
      padding: 6px !important; box-sizing: border-box !important;
      border-radius: 10px !important; overflow: hidden !important;
      background: #f2f1ee !important;
    }
    .cd-product-item-content .opus-product-item-image {
      width: 100% !important; height: 100% !important;
      object-fit: contain !important;
      mix-blend-mode: multiply; /* white product photos blend into the grey tile */
    }

    .cd-product-item-content .opus-product-item-details { margin-left: 12px !important; }
    .cd-product-item-content .cd-product-item-title-container { padding-right: 76px !important; }
    .cd-product-item-content .cd-product-item-title {
      font-size: 14px !important; font-weight: 600 !important; line-height: 1.3 !important;
    }

    /* price top right, delete bottom right */
    .cd-product-item-content .opus-price-display {
      position: absolute !important; top: 0; right: 0;
      font-size: 14px !important; font-weight: 700 !important;
    }
    .cd-product-item-content .cd-product-item-delete {
      position: absolute !important; right: 0; bottom: 6px;
      width: 18px !important; height: 18px !important;
      min-width: 18px !important; min-height: 18px !important;
    }

    /* quantity stepper as a small bordered box */
    .cd-product-item-content .cd-number-input-container {
      border: 0 !important; padding: 0 !important; margin-top: 8px !important;
    }
    .cd-product-item-content .cd-number-input {
      display: inline-flex !important; align-items: center;
      height: 32px !important; padding: 0 !important;
      border: 1px solid rgba(0, 0, 0, 0.18) !important;
      border-radius: 8px !important; background: #fff !important;
      box-shadow: none !important;
    }
    .cd-product-item-content .cd-number-input .cd-number-input-button {
      width: 30px !important; height: 30px !important; min-width: 30px !important;
    }
    .cd-product-item-content .cd-number-input .cd-number-input-field {
      width: 28px !important; height: 30px !important;
      border: 0 !important; padding: 0 !important;
      text-align: center; font-size: 14px !important;
    }

    /* Compact recommendations ("Vi rekommenderar även") */
    .opus-upsell-carousel .cd-upsell-title,
    .cd-upsell-title {
      margin: 0 0 4px !important;
      font-size: 15px !important; font-style: normal !important;
      font-weight: 500 !important; line-height: 1.3 !important;
    }
    .opus-list-upsell { margin-top: 8px !important; gap: 8px !important; }
    .opus-upsell-carousel .cd-carousel-product {
      gap: 12px !important; padding: 8px 4px !important;
      align-items: center !important;
    }
    .opus-upsell-carousel .cd-carousel-img {
      width: 56px !important; height: 56px !important;
      max-width: 56px !important; flex-shrink: 0;
      padding: 4px !important; box-sizing: border-box !important;
      object-fit: contain !important;
      border-radius: 8px !important; background: #fff !important;
      filter: brightness(0.955); /* white photo + tile become light grey */
    }
    .opus-upsell-carousel .cd-carousel-details { min-width: 0; }
    .opus-upsell-carousel .cd-carousel-details h3,
    .opus-upsell-carousel .cd-product-title {
      margin: 0 !important;
      font-size: 14px !important; font-weight: 600 !important; line-height: 1.3 !important;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .opus-upsell-carousel .cd-item-price {
      padding-top: 2px !important; font-size: 13px !important; font-weight: 500 !important;
    }
    .opus-upsell-carousel .cd-carousel-options { margin-top: 0 !important; gap: 8px !important; }

    /* Buttons black like the rest of the theme, instead of Opus' red */
    .cd-checkout-section-button,
    .opus-btn-checkout,
    .opus-checkout-btn,
    .opus-upsell-carousel .cd-buy-button,
    .opus-upsell-carousel .add-to-cart-upsell-button {
      background: #292d29 !important; background-color: #292d29 !important;
      border-color: #292d29 !important; color: #f9f7f1 !important;
    }
    .cd-checkout-section-button *,
    .opus-btn-checkout *,
    .opus-checkout-btn *,
    .opus-upsell-carousel .cd-buy-button *,
    .opus-upsell-carousel .add-to-cart-upsell-button * { color: #f9f7f1 !important; }
    .opus-upsell-carousel .ego-slider-arrow,
    .opus-upsell-carousel .ego-slider-arrow * { color: #292d29 !important; }

    /* small cart-icon button on the right, vertically centred */
    .opus-upsell-carousel .cd-carousel-details { padding-right: 44px !important; }
    .opus-upsell-carousel .cd-buy-button,
    .opus-upsell-carousel .add-to-cart-upsell-button {
      position: absolute !important; right: 4px; top: 50%;
      transform: translateY(-50%) !important;
      width: 34px !important; height: 34px !important; min-width: 34px !important;
      margin: 0 !important; padding: 0 !important;
      display: flex !important; align-items: center; justify-content: center;
      border-radius: 8px !important;
    }
    .opus-upsell-carousel .cd-buy-button svg,
    .opus-upsell-carousel .add-to-cart-upsell-button svg { width: 18px !important; height: 18px !important; }
    .opus-upsell-carousel .cd-buy-button i { font-size: 16px !important; }

    /* small arrows, no dots */
    .opus-upsell-carousel .ego-slider-arrow {
      width: 24px !important; height: 24px !important; min-width: 24px !important;
    }
    .opus-upsell-carousel .ego-slider-arrow svg { width: 14px !important; height: 14px !important; }
    .opus-upsell-carousel .ego-slider-dots { display: none !important; }
  `;

  const html = {};
  let controller = null;

  const root = () => document.getElementById('opus-shadow-container')?.shadowRoot || document;

  function injectStyle(scope) {
    if (scope.getElementById?.(STYLE_ID) || scope.querySelector?.(`#${STYLE_ID}`)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    (scope === document ? document.head : scope).appendChild(style);
  }

  function placePart(scope, part) {
    scope.querySelectorAll(`[${part.mark}]`).forEach((el) => el.remove());
    if (!html[part.mark]) return;
    for (const sel of part.anchors) {
      const anchor = scope.querySelector(sel);
      if (anchor) {
        anchor.insertAdjacentHTML('beforebegin', html[part.mark]);
        return;
      }
    }
  }

  function placeAll() {
    const scope = root();
    injectStyle(scope);
    PARTS.forEach((part) => placePart(scope, part));
  }

  // Fade our bar and totals while fresh numbers load, so they don't look wrong in the meantime
  function setUpdating(on) {
    root()
      .querySelectorAll(PARTS.map((part) => `[${part.mark}]`).join(','))
      .forEach((el) => el.classList.toggle('is-updating', on));
  }

  function refresh() {
    // A newer cart change makes a running request stale: cancel it and fetch right away
    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;
    setUpdating(true);

    const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    fetch(`${cartUrl}?sections=${SECTION},cart-icon-bubble`, { signal })
      .then((res) => res.json())
      .then((data) => {
        // Header cart count: Opus only updates an existing bubble, so an empty cart (no bubble) never gets one
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data['cart-icon-bubble']) {
          const icon = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          const fresh = icon.querySelector('.shopify-section');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }

        const doc = new DOMParser().parseFromString(data[SECTION] || '', 'text/html');
        PARTS.forEach((part) => {
          html[part.mark] = doc.querySelector(`[${part.mark}]`)?.outerHTML || '';
        });
        placeAll();
      })
      .catch(() => {
        if (!signal.aborted) setUpdating(false);
      });
  }

  // Re-fetch whenever the cart changes. Listen to Opus' own events instead of wrapping
  // window.fetch: Opus sends every quantity change through fetch, and a wrapper there
  // can leave its drawer stuck in the loading state.
  ['OpusCartChange', 'OpusAddToCartComplete'].forEach((name) => window.addEventListener(name, refresh));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  }

  // Opus re-renders its drawer often: put our parts back whenever they disappear
  let observed = null;
  const observeDrawer = () => {
    const scope = root();
    if (scope === observed) return;
    observed = scope;
    // Browser translation rewrites the drawer's text nodes, which breaks Opus' re-render
    // (quantity spinner never finishes). Keep the drawer untranslated.
    const host = document.getElementById('opus-shadow-container');
    if (host) {
      host.setAttribute('translate', 'no');
      host.classList.add('notranslate');
    }
    injectStyle(scope);
    // Empty cart "Fortsätt shoppa": Opus only closes the drawer, send shoppers to all products instead
    scope.addEventListener('click', (event) => {
      // +/− or delete: show the updating state straight away, before Opus' request returns
      if (event.target.closest?.('.cd-number-input-button, .cd-product-item-delete')) {
        setUpdating(true);
        return;
      }
      if (!event.target.closest?.('.cd-product-list-empty .btn-continue')) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.href = `${window.Shopify?.routes?.root || '/'}${CONTINUE_URL}`;
    }, true);
    // Only re-insert parts that are actually gone. Re-placing all parts here (remove + insert
    // the ones still present) triggers this observer again, and when a missing part has no
    // anchor (e.g. the checkout button is gone after removing the last item) it loops forever.
    new MutationObserver(() => {
      const missing = PARTS.filter((part) => html[part.mark] && !scope.querySelector(`[${part.mark}]`));
      if (!missing.length) return;
      injectStyle(scope);
      missing.forEach((part) => placePart(scope, part));
    }).observe(scope, { childList: true, subtree: true });
  };

  new MutationObserver(observeDrawer).observe(document.body, { childList: true });
  observeDrawer();
  refresh();
})();
